import { useId } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * A fact row that opens — TROOP-116.
 *
 * SPEC-GAP: §6 lists Button, Chip, Card, FactRow, ParticipantChip, ComplianceNotice,
 * AnnouncementCard, MessageBubble, EmptyState and no disclosure. Per §10 ("prefer the nearest
 * specified pattern") this is {@link FactRow}'s layout with a chevron and a button wrapper, and
 * nothing else: the same 100px label column, the same `--text-label` muted label, the same
 * `--text-fact` primary value, the same 44px minimum and 1px `--color-border-subtle` rule. No new
 * colour, size or radius.
 *
 * **The point is that the seam is invisible.** Above these rows sit Depart, Return and Where; below
 * the last of them sits the 3px rule and the thread. A reader scans one unbroken column of labels
 * down the left edge and cannot tell where the facts they read end and the facts they can act on
 * begin — until they notice the chevrons. That is what makes the docket read as the fact table's
 * second half rather than as a stack of cards bolted underneath it.
 *
 * **The value is the answer, not a category.** A closed row says "3 of 8" or "6 seats, 6 spare",
 * so somebody can tell whether they are sorted for a trip without opening anything.
 * A row whose closed state says only "Packing" has moved information behind a click and called it
 * simplification.
 *
 * Open state is the chevron plus `aria-expanded`, never colour — the same reasoning §8 gives for
 * the event palette. The panel is unmounted rather than hidden, which is what makes each section's
 * fetch lazy: an officer's page used to cost seventeen requests before anybody asked to see
 * anything.
 */
export function DocketRow({
  label,
  value,
  icon: Icon,
  open,
  onToggle,
  children,
}: {
  /** One word, so it holds the 100px column at 12px uppercase. */
  label: string;
  /** What the row answers while closed. Wraps rather than shrinking, per §8. */
  value: ReactNode;
  icon?: LucideIcon;
  open: boolean;
  onToggle: () => void;
  /** The panel. Only called when open, so its data is fetched on demand. */
  children: ReactNode;
}) {
  const panelId = useId();

  return (
    <div style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="grid items-start w-full text-left"
        style={{
          gridTemplateColumns: '100px 1fr auto',
          gap: 'var(--space-3)',
          minHeight: '44px',
          padding: 'var(--space-3) 0',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        <span
          className="t-label flex items-center gap-2"
          style={{
            // Muted when closed and ink when open, so an opened row reads as the heading of what
            // is beneath it. Shape and text carry the state; this only follows them.
            color: open ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
            paddingTop: '2px',
          }}
        >
          {/* Never squeezed, for the reason `FactRow`'s says (TROOP-177): an SVG with no flex
              basis gives up its own width before the text beside it wraps, so a label too long
              for the 100px column deforms its icon instead of overflowing -- and the row that
              looks wrong is the one with the small icon. Every label here is short enough today;
              the guard is so a longer one fails visibly rather than quietly. */}
          {Icon && <Icon size={14} aria-hidden="true" className="shrink-0" />}
          {label}
        </span>

        <span className="t-fact" style={{ color: 'var(--color-text-primary)' }}>
          {value}
        </span>

        <span
          className="flex items-center"
          style={{ color: 'var(--color-text-muted)', paddingTop: '2px' }}
        >
          {open ? (
            <ChevronDown size={16} aria-hidden="true" />
          ) : (
            <ChevronRight size={16} aria-hidden="true" />
          )}
        </span>
      </button>

      {open && (
        <div id={panelId} style={{ paddingBottom: 'var(--space-5)' }}>
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * The management block inside a panel, and the control that reveals it.
 *
 * **Configuration is activated, never resting.** An officer opening "Getting there" reads the legs
 * first — the same thing everybody else reads — and the controls that change them appear only when
 * asked for. Before TROOP-116 every management surface on the event page was expanded by default,
 * so a leader met five of them before reaching the thread.
 *
 * Uniform across every panel on purpose. A leader learns one gesture rather than discovering that
 * transport reveals its controls and packing simply shows them.
 */
export function ManageBlock({
  label,
  open,
  onToggle,
  children,
}: {
  /** A verb phrase naming what opens — "Manage what to bring". Sentence case, per §6. */
  label: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const panelId = useId();

  return (
    <div
      style={{
        marginTop: 'var(--space-4)',
        paddingTop: 'var(--space-3)',
        borderTop: '1px solid var(--color-border-subtle)',
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="t-label inline-flex items-center gap-2"
        style={{
          minHeight: 'var(--tap-min)',
          background: 'none',
          border: 'none',
          color: 'var(--color-text-muted)',
          cursor: 'pointer',
        }}
      >
        {open ? (
          <ChevronDown size={16} aria-hidden="true" />
        ) : (
          <ChevronRight size={16} aria-hidden="true" />
        )}
        {label}
      </button>

      {open && (
        <div
          id={panelId}
          className="flex flex-col"
          style={{ gap: 'var(--space-4)', marginTop: 'var(--space-3)' }}
        >
          {children}
        </div>
      )}
    </div>
  );
}
